import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, NavLinkProps, useNavigate } from "react-router-dom";
import ic_dome from "../assets/images/icons/dome-logo.svg";
import ic_small_dome from "../assets/images/icons/favicon.png";
import ic_appointments_b from "../assets/images/icons/ic-Appointments.svg";
import ic_appointments from "../assets/images/icons/ic-Appointments2.svg";
import ic_Home_b from "../assets/images/icons/ic-Home2-active.svg";
import ic_Home from "../assets/images/icons/ic-Home2.svg";
import ic_descriptions from "../assets/images/icons/ic_descriptions.svg";
import ic_descriptions_b from "../assets/images/icons/ic_descriptions_b.svg";
import icon_patients_list from "../assets/images/icons/Patients-unactive.svg";
import icon_patients_list_b from "../assets/images/icons/Patients.svg";
import RecordsTemplates from "../assets/images/icons/Records-Templates.svg";
import RecordsTemplates_b from "../assets/images/icons/RecordsTemplates_b.svg";
import ic_Setting_b from "../assets/images/icons/setting-active.svg";
import ic_Setting from "../assets/images/icons/setting.svg";
import ic_profile_b from "../assets/images/icons/user-profile-03-active.svg";
import ic_profile from "../assets/images/icons/user-profile-03.svg";
import closeIcon from "../assets/images/icons/close_sidebar.svg";
import headphone from "../assets/images/headphone.png";
import { toggleSidebar } from "../redux/reducers/sidebarSlice";
import { RootState } from "../redux/store";
import { isArabic } from "../utils/utils";
import i18next from "i18next";
import { IsProfileCompleted } from "../Share";
import { useGetPersonalInfo } from "../hooks";
interface NavItem {
  to?: string;
  icon?: string;
  iconActive?: string;
  label: string;
  smallLabel?: string;
  cannotGoToRoute?: boolean;
}

interface SidebarLinkProps extends NavLinkProps {
  icon: string;
  iconActive: string;
  label: string;
  smallLabel?: string;
  className?: string;
  isDisabled?: boolean;
  onDisabledClick?: () => void;
  cannotGoToRoute?: boolean;
}

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.user);
  const { isSidebarCollapsed } = useSelector(
    (state: RootState) => state.sidebar,
  );
  const { isAllowed } = useGetPersonalInfo();

  const navItems: NavItem[] = [
    {
      to: "/",
      icon: ic_Home,
      iconActive: ic_Home_b,
      label: "home",
      smallLabel: "home",
    },
    {
      to: "/profile",
      icon: ic_profile,
      iconActive: ic_profile_b,
      label: "profile",
      smallLabel: "profile",
    },
    {
      to: "/patients_list",
      icon: icon_patients_list,
      iconActive: icon_patients_list_b,
      label: "my_patients",
      smallLabel: "patients",
    },
    {
      to: "/appointments",
      icon: ic_appointments,
      iconActive: ic_appointments_b,
      label: "my_appointments",
      smallLabel: "appointments",
    },

    {
      to: "/medical-record-templates",
      icon: RecordsTemplates,
      iconActive: RecordsTemplates_b,
      label: "medical_record_templates",
      smallLabel: "templates",
      cannotGoToRoute: !isAllowed,
    },
    {
      to: "/my_prescriptions",
      icon: ic_descriptions,
      iconActive: ic_descriptions_b,
      label: "my_prescriptions",
      smallLabel: "prescriptions",
    },
    {
      to: "/setting",
      icon: ic_Setting,
      iconActive: ic_Setting_b,
      label: "setting",
    },
  ];

  const handleCloseSidebar = () => {
    dispatch(toggleSidebar());
  };

  return (
    <nav
      className={classNames(
        "fixed bottom-0 z-10 m-0 shrink-0 bg-white text-body-text transition-all duration-300 xl:static xl:block xl:h-screen",
        {
          "xl:w-20": isSidebarCollapsed,
          "xl:w-auto": !isSidebarCollapsed,
          "h-[60px] w-full xl:h-screen": !isSidebarCollapsed,
        },
      )}
    >
      <div className="flex h-20 items-center justify-between p-6 max-xl:hidden">
        <img
          src={ic_dome}
          alt="Dome Care Logo"
          className={classNames(
            "h-9 cursor-pointer transition-all duration-300",
            {
              "w-9 xl:invisible xl:w-0 xl:opacity-0": isSidebarCollapsed,
              "w-16": !isSidebarCollapsed,
            },
          )}
          onClick={() => navigate("/")}
        />
        <img
          src={closeIcon}
          alt="Toggle sidebar"
          className={` hidden cursor-pointer xl:block 
            ${isArabic(i18next?.language) ? "rotate-180" : ""} ${isArabic(i18next?.language) 
              && isSidebarCollapsed ? "rotate-0" : ""} ${isSidebarCollapsed ? "rotate-180" : ""}
               transition-transform duration-300`}
          onClick={handleCloseSidebar}
        />
      </div>
      <hr className={isSidebarCollapsed ? "mb-2" : ""} />

      <div
        className={classNames(
          "flex h-full w-full justify-between gap-2 p-6 max-xl:my-1 max-xl:items-center max-xl:p-3 xl:flex-col xl:justify-start",
          {
            "xl:items-center xl:p-4": isSidebarCollapsed,
          },
        )}
      >
        {navItems.map((item, index) => (
          <div key={index}>
            <SidebarLink
              to={isAuthenticated ? item.to! : "#"}
              icon={item.icon!}
              iconActive={item.iconActive!}
              label={item.label}
              smallLabel={item.smallLabel}
              isDisabled={!isAuthenticated}
              isCollapsed={isSidebarCollapsed}
              cannotGoToRoute={item.cannotGoToRoute}
            />
          </div>
        ))}
        {!isSidebarCollapsed && (
          <div className="mb-24 mt-auto flex w-60 flex-col gap-4 rounded-lg bg-InputFieldColor p-4 text-primary-text max-xl:hidden">
            <div className="flex items-center justify-center">
              <div>
                <p className="text-sm font-bold max-xl:hidden">
                  {t("need_help")}
                </p>
                <p className="text-xs max-xl:hidden">
                  {t("our_support_team_is_here_for_you")}
                </p>
              </div>
              <div className="rounded-full bg-input-field p-2">
                <div className="rounded-full bg-white p-2">
                  <img src={headphone} alt="Headphone" className="h-" />
                </div>
              </div>
            </div>
            <button
              className="w-fit cursor-pointer rounded-lg border bg-white px-4 py-1 text-primary"
              onClick={() => navigate("/setting/about_dome_care")}
            >
              {t("contact_us")}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Sidebar;

const SidebarLink: React.FC<SidebarLinkProps & { isCollapsed?: boolean }> = ({
  to,
  icon,
  iconActive,
  label,
  smallLabel,
  className,
  isDisabled,
  onDisabledClick,
  isCollapsed = false,
  cannotGoToRoute,
  ...props
}) => {
  const { t } = useTranslation();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isDisabled) {
      e.preventDefault();
      onDisabledClick?.();
    }
  };

  return cannotGoToRoute ? (
    <IsProfileCompleted title="complete_your_profile_to_go_to_this_path">
      <div
        className={classNames(
          "relative flex items-center gap-3 rounded-lg border p-3 transition-all duration-200 max-xl:flex-col max-xl:gap-1 max-xl:p-2",
          "border-white text-body-text hover:bg-primary-bg hover:text-primary-text",
          {
            "justify-center": isCollapsed,
            "justify-start": !isCollapsed,
          },
          className,
        )}
      >
        <img
          className="h-6 transition-transform duration-200 max-xl:h-5"
          alt={t(label)}
          src={icon}
        />

        {isCollapsed && (
          <span className="absolute left-full ml-2 hidden whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity duration-200 xl:block">
            {t(label)}
          </span>
        )}

        {!isCollapsed && (
          <>
            <span className="max-xl:hidden">{t(label)}</span>
            <span className="text-xs xl:hidden">{t(smallLabel || label)}</span>
          </>
        )}
      </div>
    </IsProfileCompleted>
  ) : (
    <NavLink
      to={to}
      end={false}
      onClick={handleClick}
      className={({ isActive }) =>
        classNames(
          "relative flex items-center gap-3 rounded-lg border p-3 transition-all duration-200 max-xl:flex-col max-xl:gap-1 max-xl:p-2",
          "border-white hover:text-primary-text xl:hover:bg-primary-click",
          {
            "active border font-bold text-primary-text max-xl:!text-primary xl:!border-primary xl:bg-primaryLight":
              !isDisabled && isActive,
            "justify-center": isCollapsed,
            "justify-start": !isCollapsed,
          },
          className,
        )
      }
      {...props}
    >
      {({ isActive }) => {
        const shouldShowActiveIcon = !isDisabled && isActive;
        const currentIcon = shouldShowActiveIcon ? iconActive : icon;

        return (
          <>
            <img
              className="h-6 transition-transform duration-200 max-xl:h-5"
              alt={t(label)}
              src={currentIcon}
            />

            {isCollapsed && (
              <span className="absolute left-full ml-2 hidden whitespace-nowrap rounded-md bg-gray-800 px-2 py-2 text-xs text-white opacity-0 transition-opacity duration-200 xl:block">
                {t(label)}
              </span>
            )}

            {!isCollapsed && (
              <>
                <span className="max-xl:hidden">{t(label)}</span>
                <span className="text-xs xl:hidden">
                  {t(smallLabel || label)}
                </span>
              </>
            )}
          </>
        );
      }}
    </NavLink>
  );
};
